import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

// Get Firestore instance lazily to avoid initialization errors
const getDb = () => firebase.firestore();

// Points configuration
const POINTS_CONFIG = {
  // Earning points
  COPPER_NOTE: 1,
  NICKEL_NOTE: 2,
  BRONZE_NOTE: 10,
  SILVER_NOTE: 25,
  GOLD_NOTE: 100,
  PLATINUM_NOTE: 1000,
  COLLECTIBLE_PICKUP_BONUS: 5, // Bonus when someone else picks up your collectible

  // Spending points
  EXTEND_STATIC_NOTE_24H: 10, // Cost to extend static note by 24 hours

  // Levels (based on total points earned)
  LEVELS: [
    { level: 1, minPoints: 0, name: 'Novice' },
    { level: 2, minPoints: 100, name: 'Explorer' },
    { level: 3, minPoints: 250, name: 'Adventurer' },
    { level: 4, minPoints: 500, name: 'Seeker' },
    { level: 5, minPoints: 1000, name: 'Collector' },
    { level: 6, minPoints: 2500, name: 'Hunter' },
    { level: 7, minPoints: 5000, name: 'Master' },
    { level: 8, minPoints: 10000, name: 'Legend' },
    { level: 9, minPoints: 25000, name: 'Champion' },
    { level: 10, minPoints: 50000, name: 'Elite' },
  ],
};

class PointsService {
  /**
   * Initialize user points document if it doesn't exist
   * @param {string} userId
   */
  async initializeUserPoints(userId) {
    const db = getDb();
    const userPointsRef = doc(db, 'userPoints', userId);
    const userPointsDoc = await getDoc(userPointsRef);

    if (!userPointsDoc.exists()) {
      await setDoc(userPointsRef, {
        userId,
        currentPoints: 0,
        totalPointsEarned: 0,
        totalPointsSpent: 0,
        level: 1,
        levelName: 'Novice',
        notesCollected: 0,
        notesPlaced: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  }

  /**
   * Get user points data
   * @param {string} userId
   * @returns {Promise<Object>}
   */
  async getUserPoints(userId) {
    await this.initializeUserPoints(userId);
    const db = getDb();
    const userPointsRef = doc(db, 'userPoints', userId);
    const userPointsDoc = await getDoc(userPointsRef);
    return userPointsDoc.data();
  }

  /**
   * Calculate user level based on total points earned
   * @param {number} totalPoints
   * @returns {Object} - { level, levelName }
   */
  calculateLevel(totalPoints) {
    let currentLevel = POINTS_CONFIG.LEVELS[0];

    for (const levelConfig of POINTS_CONFIG.LEVELS) {
      if (totalPoints >= levelConfig.minPoints) {
        currentLevel = levelConfig;
      } else {
        break;
      }
    }

    return {
      level: currentLevel.level,
      levelName: currentLevel.name,
    };
  }

  /**
   * Add points to user account
   * @param {string} userId
   * @param {number} points
   * @param {string} reason - Reason for earning points
   * @returns {Promise<Object>} - Updated user points data
   */
  async addPoints(userId, points, reason = '') {
    await this.initializeUserPoints(userId);
    const db = getDb();
    const userPointsRef = doc(db, 'userPoints', userId);

    // Update points
    await updateDoc(userPointsRef, {
      currentPoints: increment(points),
      totalPointsEarned: increment(points),
      updatedAt: serverTimestamp(),
    });

    // Get updated data and recalculate level
    const updatedDoc = await getDoc(userPointsRef);
    const data = updatedDoc.data();
    const { level, levelName } = this.calculateLevel(data.totalPointsEarned);

    // Update level if changed
    if (level !== data.level) {
      await updateDoc(userPointsRef, {
        level,
        levelName,
      });
      console.log(`🎉 Level up! You are now level ${level}: ${levelName}`);
    }

    // Log transaction
    await this.logTransaction(userId, points, 'earn', reason);

    console.log(`✨ +${points} points earned: ${reason}`);
    return { ...data, currentPoints: data.currentPoints + points, level, levelName };
  }

  /**
   * Spend points from user account
   * @param {string} userId
   * @param {number} points
   * @param {string} reason - Reason for spending points
   * @returns {Promise<boolean>} - Success/failure
   */
  async spendPoints(userId, points, reason = '') {
    await this.initializeUserPoints(userId);
    const db = getDb();
    const userPointsRef = doc(db, 'userPoints', userId);
    const userPointsDoc = await getDoc(userPointsRef);
    const data = userPointsDoc.data();

    // Check if user has enough points
    if (data.currentPoints < points) {
      console.error('Insufficient points');
      return false;
    }

    // Deduct points
    await updateDoc(userPointsRef, {
      currentPoints: increment(-points),
      totalPointsSpent: increment(points),
      updatedAt: serverTimestamp(),
    });

    // Log transaction
    await this.logTransaction(userId, points, 'spend', reason);

    console.log(`💰 -${points} points spent: ${reason}`);
    return true;
  }

  /**
   * Log a points transaction
   * @param {string} userId
   * @param {number} points
   * @param {string} type - 'earn' or 'spend'
   * @param {string} reason
   */
  async logTransaction(userId, points, type, reason) {
    const db = getDb();
    const transactionRef = db.collection('pointsTransactions');
    await transactionRef.add({
      userId,
      points,
      type,
      reason,
      timestamp: serverTimestamp(),
    });
  }

  /**
   * Award points for collecting a tiered note
   * @param {string} userId
   * @param {string} tier - bronze, silver, gold, platinum
   * @returns {Promise<number>} - Points awarded
   */
  async awardTieredNotePoints(userId, tier) {
    const pointsMap = {
      copper: POINTS_CONFIG.COPPER_NOTE,
      nickel: POINTS_CONFIG.NICKEL_NOTE,
      bronze: POINTS_CONFIG.BRONZE_NOTE,
      silver: POINTS_CONFIG.SILVER_NOTE,
      gold: POINTS_CONFIG.GOLD_NOTE,
      platinum: POINTS_CONFIG.PLATINUM_NOTE,
    };

    const points = pointsMap[tier] || 0;
    if (points > 0) {
      await this.addPoints(userId, points, `Collected ${tier} note`);

      // Increment notes collected counter
      const db = getDb();
      const userPointsRef = doc(db, 'userPoints', userId);
      await updateDoc(userPointsRef, {
        notesCollected: increment(1),
      });
    }

    return points;
  }

  /**
   * Extend static note expiry by 24 hours
   * @param {string} userId
   * @param {string} noteId
   * @returns {Promise<boolean>} - Success/failure
   */
  async extendNoteExpiry(userId, noteId) {
    const cost = POINTS_CONFIG.EXTEND_STATIC_NOTE_24H;
    const success = await this.spendPoints(userId, cost, `Extended note ${noteId} expiry`);

    if (success) {
      // Update note expiry in database
      const db = getDb();
      const noteRef = doc(db, 'tokens', noteId);
      const noteDoc = await getDoc(noteRef);

      if (noteDoc.exists()) {
        const currentExpiry = noteDoc.data().expiresAt;
        const newExpiry = new Date(currentExpiry);
        newExpiry.setHours(newExpiry.getHours() + 24);

        await updateDoc(noteRef, {
          expiresAt: newExpiry,
          updatedAt: serverTimestamp(),
        });

        console.log(`📝 Note expiry extended to ${newExpiry}`);
        return true;
      }
    }

    return false;
  }

  /**
   * Increment notes placed counter
   * @param {string} userId
   */
  async incrementNotesPlaced(userId) {
    await this.initializeUserPoints(userId);
    const db = getDb();
    const userPointsRef = doc(db, 'userPoints', userId);
    await updateDoc(userPointsRef, {
      notesPlaced: increment(1),
    });
  }

  /**
   * Get points cost for extending note expiry
   * @returns {number}
   */
  getExtendCost() {
    return POINTS_CONFIG.EXTEND_STATIC_NOTE_24H;
  }
}

// Export singleton instance
export default new PointsService();
export { POINTS_CONFIG };
